
import React, { useState, useEffect, useRef } from 'react';
import type { Athlete, RaceEvent, RaceCategory, EventType, RecurrenceFrequency, SkillLevel, GroupRideDetails } from '../types';

/**
 * Minimal shape used to pre-populate a new RaceEditorModal when creating
 * a race from an external event (e.g. BikeReg).
 */
export interface InitialRaceData {
    name?: string;
    date?: string;
    category?: string;
    isCircuit?: boolean;
}
import { XIcon, TrashIcon, MapPinIcon, PlusIcon } from './icons/PlaybackIcons';
import ConfirmationModal from './ConfirmationModal';
import { useAuth } from '../contexts/AuthContext';
import RaceLocationPickerMap from './RaceLocationPickerMap';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { raceEventService } from '../services/raceService';
import { parseGpxData, parseTcxData, parseFitData } from '../services/dataParser';
import { normalizeSector } from '../utils/raceSector';
import { trimRouteSegment } from '../utils/routeTrimmer';

interface RaceEditorModalProps {
    onClose: () => void;
    onSave: (event: RaceEvent) => void;
    onDelete?: (event: RaceEvent) => void;
    eventToEdit?: RaceEvent;
    initialData?: InitialRaceData | null;
}

interface CategoryRow {
    id: string; // Temporary ID for list management
    categoryName: string;
    startTime: string; // "HH:MM" (Scheduled)
    durationMins: number;
    distanceMiles: number;
    originalRaceId?: string; // If editing an existing race doc
    athletesCount?: number; // Keep track to prevent accidental deletion of data
}

const resizeImage = (file: File, maxWidth: number, maxHeight: number): Promise<string> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = (event) => {
            const img = new Image();
            img.src = event.target?.result as string;
            img.onload = () => {
                const canvas = document.createElement('canvas');
                let width = img.width;
                let height = img.height;

                if (width > height) {
                    if (width > maxWidth) {
                        height *= maxWidth / width;
                        width = maxWidth;
                    }
                } else {
                    if (height > maxHeight) {
                        width *= maxHeight / height;
                        height = maxHeight;
                    }
                }

                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext('2d');
                if (ctx) {
                    ctx.drawImage(img, 0, 0, width, height);
                    resolve(canvas.toDataURL('image/jpeg', 0.8));
                } else {
                    reject(new Error('Canvas context not available'));
                }
            };
            img.onerror = (err) => reject(err);
        };
        reader.onerror = (err) => reject(err);
    });
};

const generateSlug = (text: string): string => {
    return text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || `id-${Date.now()}`;
};

const DAYS_OF_WEEK = [
    { value: 0, short: 'S', label: 'Sunday' },
    { value: 1, short: 'M', label: 'Monday' },
    { value: 2, short: 'T', label: 'Tuesday' },
    { value: 3, short: 'W', label: 'Wednesday' },
    { value: 4, short: 'T', label: 'Thursday' },
    { value: 5, short: 'F', label: 'Friday' },
    { value: 6, short: 'S', label: 'Saturday' },
];

const getDayOfWeekFromDateString = (dateStr: string): number => {
    if (!dateStr || !/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return 0;
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return date.getDay();
};

const formatDateToString = (date: Date): string => {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const calculateMatchingDate = (baseDateStr: string | undefined, targetDayOfWeek: number): string => {
    let baseDate: Date;
    if (baseDateStr && /^\d{4}-\d{2}-\d{2}$/.test(baseDateStr)) {
        const [year, month, day] = baseDateStr.split('-').map(Number);
        baseDate = new Date(year, month - 1, day);
    } else {
        baseDate = new Date();
    }

    const currentDay = baseDate.getDay();
    let diff = targetDayOfWeek - currentDay;

    if (diff < 0) {
        diff += 7;
    }

    baseDate.setDate(baseDate.getDate() + diff);
    return formatDateToString(baseDate);
};

const getRecurrenceSummaryText = (
    freq: RecurrenceFrequency,
    dow: number,
    dateStr: string
): string => {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const dayName = days[dow] || 'day';

    let dateFormatted = '';
    if (dateStr && /^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
        const [year, month, day] = dateStr.split('-').map(Number);
        const d = new Date(year, month - 1, day);
        dateFormatted = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    }

    const startSuffix = dateFormatted ? ` starting ${dateFormatted}` : '';

    if (freq === 'weekly') {
        return `Repeats every ${dayName}${startSuffix}`;
    } else if (freq === 'biweekly') {
        return `Repeats every 2 weeks on ${dayName}${startSuffix}`;
    } else if (freq === 'monthly') {
        if (dateStr && /^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
            const [, , day] = dateStr.split('-').map(Number);
            const nth = Math.ceil(day / 7);
            const ordinals = ['1st', '2nd', '3rd', '4th', '5th'];
            const nthStr = ordinals[nth - 1] || `${nth}th`;
            return `Repeats monthly on the ${nthStr} ${dayName}${startSuffix}`;
        }
        return `Repeats monthly on ${dayName}${startSuffix}`;
    }
    return `Repeats on ${dayName}${startSuffix}`;
};

export const formatTime12h = (timeStr?: string): string => {
    if (!timeStr) return '';
    if (/am|pm/i.test(timeStr)) return timeStr;
    const parts = timeStr.split(':');
    if (parts.length < 2) return timeStr;
    let hours = parseInt(parts[0], 10);
    const minutes = parts[1].padStart(2, '0');
    if (isNaN(hours)) return timeStr;
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    if (hours === 0) hours = 12;
    const hoursStr = hours.toString().padStart(2, '0');
    return `${hoursStr}:${minutes} ${ampm}`;
};

export const parseTimeToMinutes = (timeStr?: string): number | null => {
    if (!timeStr) return null;
    const match = timeStr.match(/^(\d{1,2}):(\d{2})(?:\s*(AM|PM))?$/i);
    if (!match) return null;
    let hours = parseInt(match[1], 10);
    const minutes = parseInt(match[2], 10);
    const ampm = match[3]?.toUpperCase();

    if (ampm) {
        if (ampm === 'PM' && hours < 12) hours += 12;
        if (ampm === 'AM' && hours === 12) hours = 0;
    }
    return hours * 60 + minutes;
};

const getMeetupBufferText = (meetup: string, roll: string): string => {
    if (!meetup || !roll) return '';
    const meetupMins = parseTimeToMinutes(meetup);
    const rollMins = parseTimeToMinutes(roll);
    if (meetupMins === null || rollMins === null) return '';
    const diff = rollMins - meetupMins;
    if (diff < 0) return `Roll time (${formatTime12h(roll)}) cannot be earlier than meetup time (${formatTime12h(meetup)})`;
    if (diff === 0) return 'Rolls immediately at meetup time';
    const hours = Math.floor(diff / 60);
    const mins = diff % 60;
    if (hours > 0 && mins > 0) {
        return `${hours} hr ${mins} min window before roll`;
    } else if (hours > 0) {
        return `${hours} hr window before roll`;
    } else {
        return `${mins} min meetup buffer before roll`;
    }
};

export const COMMON_TIMEZONES = [
    { value: 'America/Los_Angeles', label: 'Pacific Time (PT) - Los Angeles' },
    { value: 'America/Denver', label: 'Mountain Time (MT) - Denver' },
    { value: 'America/Phoenix', label: 'Mountain Standard (no DST) - Phoenix' },
    { value: 'America/Chicago', label: 'Central Time (CT) - Chicago' },
    { value: 'America/New_York', label: 'Eastern Time (ET) - New York' },
    { value: 'America/Anchorage', label: 'Alaska Time (AKT) - Anchorage' },
    { value: 'Pacific/Honolulu', label: 'Hawaii Time (HT) - Honolulu' },
    { value: 'Europe/London', label: 'GMT / BST - London' },
    { value: 'Europe/Paris', label: 'CET / CEST - Paris, Berlin' },
    { value: 'Australia/Sydney', label: 'AEST / AEDT - Sydney' },
];

export const getTimeZoneAbbreviation = (timeZone?: string, date: Date = new Date()): string => {
    if (!timeZone) return '';
    try {
        const parts = new Intl.DateTimeFormat('en-US', { timeZone, timeZoneName: 'short' }).formatToParts(date);
        return parts.find(p => p.type === 'timeZoneName')?.value || '';
    } catch {
        return '';
    }
};

const RaceEditorModal: React.FC<RaceEditorModalProps> = ({ onClose, onSave, onDelete, eventToEdit, initialData }) => {
    const { currentUser } = useAuth();

    // -- Mode Switcher State --
    const [eventType, setEventType] = useState<EventType>('race');

    // -- Event Level State --
    const [eventName, setEventName] = useState('');
    const [eventDate, setEventDate] = useState('');
    const [imageUrl, setImageUrl] = useState('');
    const [startLat, setStartLat] = useState('');
    const [startLon, setStartLon] = useState('');
    const [finishLat, setFinishLat] = useState('');
    const [finishLon, setFinishLon] = useState('');
    const [isCircuit, setIsCircuit] = useState(false);

    // -- Group Ride Specific State --
    const [frequency, setFrequency] = useState<RecurrenceFrequency>('weekly');
    const [dayOfWeek, setDayOfWeek] = useState<number>(2); // Default: Tuesday
    const [meetupTime, setMeetupTime] = useState('06:15');
    const [rollTime, setRollTime] = useState('06:30');
    const [timeZone, setTimeZone] = useState<string>(
        () => eventToEdit?.groupRideDetails?.timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York'
    );
    const [locationName, setLocationName] = useState('');
    const [isDrop, setIsDrop] = useState(false);
    const [skillLevel, setSkillLevel] = useState<SkillLevel>('advanced');
    const [hostName, setHostName] = useState('');
    const [hostUrl, setHostUrl] = useState('');
    const [infoText, setInfoText] = useState('');
    const [spiritedStartLat, setSpiritedStartLat] = useState('');
    const [spiritedStartLon, setSpiritedStartLon] = useState('');
    const [spiritedFinishLat, setSpiritedFinishLat] = useState('');
    const [spiritedFinishLon, setSpiritedFinishLon] = useState('');

    // -- Route GPX / FIT Upload State --
    const [coursePath, setCoursePath] = useState<[number, number][]>([]);
    const [rawCoursePath, setRawCoursePath] = useState<[number, number][]>([]);
    const rawTrackRef = useRef<any[]>([]);
    const [distanceMiles, setDistanceMiles] = useState<number | undefined>(undefined);
    const [rawDistanceMiles, setRawDistanceMiles] = useState<number | undefined>(undefined);
    const [elevationGainFt, setElevationGainFt] = useState<number | undefined>(undefined);
    const [rawElevationGainFt, setRawElevationGainFt] = useState<number | undefined>(undefined);
    const [elevationProfile, setElevationProfile] = useState<any[]>([]);
    const [isRouteTrimmed, setIsRouteTrimmed] = useState(false);
    const [routeFileName, setRouteFileName] = useState('');
    const [isParsingRoute, setIsParsingRoute] = useState(false);
    const routeInputRef = useRef<HTMLInputElement>(null);

    // -- Categories State --
    const [categories, setCategories] = useState<CategoryRow[]>([]);

    const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);
    const [showMap, setShowMap] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [isDetecting, setIsDetecting] = useState(false);

    // -- Template State --
    const [availableRaces, setAvailableRaces] = useState<RaceEvent[]>([]);
    const [selectedTemplateRaceId, setSelectedTemplateRaceId] = useState<string>('');
    const [isLoadingTemplate, setIsLoadingTemplate] = useState(false);

    const isEditing = !!eventToEdit;

    // Initialize State
    useEffect(() => {
        if (isEditing && eventToEdit) {
            setEventType(eventToEdit.eventType || 'race');
            setEventName(eventToEdit.name);
            setEventDate(eventToEdit.date);
            setImageUrl(eventToEdit.imageUrl || '');
            const parseCoord = (coord: any): [number, number] | null => {
                if (!coord) return null;
                if (Array.isArray(coord) && coord.length >= 2) {
                    const lat = Number(coord[0]);
                    const lon = Number(coord[1]);
                    if (!isNaN(lat) && !isNaN(lon) && (lat !== 0 || lon !== 0)) return [lat, lon];
                } else if (typeof coord === 'object') {
                    const lat = Number(coord.lat ?? coord.latitude);
                    const lon = Number(coord.lon ?? coord.lng ?? coord.longitude);
                    if (!isNaN(lat) && !isNaN(lon) && (lat !== 0 || lon !== 0)) return [lat, lon];
                }
                return null;
            };

            const sCoord = parseCoord(eventToEdit.course?.start);
            const fCoord = parseCoord(eventToEdit.course?.finish);
            setStartLat(sCoord ? sCoord[0].toString() : '');
            setStartLon(sCoord ? sCoord[1].toString() : '');
            setFinishLat(fCoord ? fCoord[0].toString() : '');
            setFinishLon(fCoord ? fCoord[1].toString() : '');
            setIsCircuit(eventToEdit.isCircuit || false);

            if (eventToEdit.course?.path) {
                setCoursePath(eventToEdit.course.path);
                setRawCoursePath(eventToEdit.course.path);
                setShowMap(true);
            }

            if (eventToEdit.groupRideDetails) {
                const gr = eventToEdit.groupRideDetails;
                setFrequency(gr.frequency || 'weekly');
                setDayOfWeek(eventToEdit.date ? getDayOfWeekFromDateString(eventToEdit.date) : (gr.dayOfWeek ?? 2));
                setMeetupTime(gr.meetupTime || '06:15');
                setRollTime(gr.rollTime || '06:30');
                if (gr.timeZone) {
                    setTimeZone(gr.timeZone);
                }
                setLocationName(gr.locationName || '');
                setIsDrop(gr.isDrop ?? false);
                setSkillLevel(gr.skillLevel || 'advanced');
                setHostName(gr.hostName || '');
                setHostUrl(gr.hostUrl || '');
                setInfoText(gr.infoText || '');
                setDistanceMiles(gr.distanceMiles);
                setRawDistanceMiles(gr.distanceMiles);
                setElevationGainFt(gr.elevationGainFt);
                setRawElevationGainFt(gr.elevationGainFt);
                if (gr.elevationProfile) setElevationProfile(gr.elevationProfile);

                if (gr.spiritedSegment) {
                    setSpiritedStartLat(gr.spiritedSegment.startLat?.toString() || '');
                    setSpiritedStartLon(gr.spiritedSegment.startLon?.toString() || '');
                    setSpiritedFinishLat(gr.spiritedSegment.finishLat?.toString() || '');
                    setSpiritedFinishLon(gr.spiritedSegment.finishLon?.toString() || '');
                }
            }

            // Populate categories from RaceEvent.categories
            const loadedCats: CategoryRow[] = eventToEdit.categories.map(cat => {
                const timeToUse = cat.scheduledStartTime || cat.startTime;
                const dateObj = new Date(timeToUse);
                const hours = dateObj.getHours().toString().padStart(2, '0');
                const minutes = dateObj.getMinutes().toString().padStart(2, '0');

                return {
                    id: cat.id,
                    categoryName: cat.name,
                    startTime: timeToUse > 0 ? `${hours}:${minutes}` : '10:00',
                    durationMins: cat.duration > 0 ? Math.round(cat.duration / 60000) : 60,
                    distanceMiles: cat.distance || 0,
                    originalRaceId: cat.id,
                    athletesCount: cat.athletes?.length || 0
                };
            });
            setCategories(loadedCats.length > 0 ? loadedCats : [{
                id: Date.now().toString(),
                categoryName: 'Group Ride',
                startTime: '06:30',
                durationMins: 90,
                distanceMiles: 0
            }]);

        } else if (initialData) {
            // Create from External
            setEventType('race');
            setEventName(initialData.name || '');
            if (initialData.date) {
                const dateString = /^\d{4}-\d{2}-\d{2}$/.test(initialData.date)
                    ? `${initialData.date}T00:00:00`
                    : initialData.date;

                const d = new Date(dateString);
                if (!isNaN(d.getTime())) {
                    setEventDate(d.toISOString().split('T')[0]);
                } else {
                    setEventDate(initialData.date);
                }
            }
            setImageUrl('');
            setStartLat('');
            setStartLon('');
            setFinishLat('');
            setFinishLon('');
            setIsCircuit(initialData.isCircuit || false);
            setCategories([{
                id: Date.now().toString(),
                categoryName: initialData.category || 'Category 1',
                startTime: '10:00',
                durationMins: 60,
                distanceMiles: 0
            }]);
        } else {
            // Fresh Create
            setEventType('race');
            setEventName('');
            setEventDate('');
            setImageUrl('');
            setStartLat('');
            setStartLon('');
            setFinishLat('');
            setFinishLon('');
            setIsCircuit(false);
            setCoursePath([]);
            setRawCoursePath([]);
            setRawDistanceMiles(undefined);
            setRawElevationGainFt(undefined);
            setIsRouteTrimmed(false);
            rawTrackRef.current = [];
            setCategories([{
                id: Date.now().toString(),
                categoryName: 'Category 1',
                startTime: '10:00',
                durationMins: 60,
                distanceMiles: 0
            }]);
        }
    }, [eventToEdit, isEditing, initialData]);

    const handleMeetupDateChange = (newDateStr: string) => {
        setEventDate(newDateStr);
        if (newDateStr && /^\d{4}-\d{2}-\d{2}$/.test(newDateStr)) {
            const dow = getDayOfWeekFromDateString(newDateStr);
            setDayOfWeek(dow);
        }
    };

    const handleDayOfWeekChange = (newDayOfWeek: number) => {
        setDayOfWeek(newDayOfWeek);
        const updatedDate = calculateMatchingDate(eventDate, newDayOfWeek);
        setEventDate(updatedDate);
    };

    const handleMeetupTimeChange = (newMeetupTime: string) => {
        setMeetupTime(newMeetupTime);
        const newMeetupMins = parseTimeToMinutes(newMeetupTime);
        const rollMins = parseTimeToMinutes(rollTime);
        if (newMeetupMins !== null && rollMins !== null && rollMins < newMeetupMins) {
            setRollTime(newMeetupTime);
        }
    };

    const handleRollTimeChange = (newRollTime: string) => {
        setRollTime(newRollTime);
        const newRollMins = parseTimeToMinutes(newRollTime);
        const meetupMins = parseTimeToMinutes(meetupTime);
        if (newRollMins !== null && meetupMins !== null && newRollMins < meetupMins) {
            setRollTime(meetupTime);
        }
    };

    // Fetch available races for the template dropdown
    useEffect(() => {
        const fetchRaces = async () => {
            try {
                const races = await raceEventService.getAllRaceEvents();
                setAvailableRaces(races);
            } catch (e) {
                console.error("Failed to load available races for templates", e);
            }
        };
        fetchRaces();
    }, []);

    const handleApplyTemplate = async () => {
        if (!selectedTemplateRaceId) return;

        const isDefault = categories.length === 1 && categories[0].categoryName === 'Category 1';
        if (!isDefault && !window.confirm("This will replace your current categories with the template. Proceed?")) {
            return;
        }

        setIsLoadingTemplate(true);
        try {
            const templateRace = await raceEventService.getRaceEventBySlug(selectedTemplateRaceId);
            if (templateRace && templateRace.categories && templateRace.categories.length > 0) {
                const newCats: CategoryRow[] = templateRace.categories.map((cat, index) => {
                    const timeToUse = cat.scheduledStartTime || cat.startTime || 0;
                    let startTimeStr = '10:00';
                    if (timeToUse > 0) {
                        const dateObj = new Date(timeToUse);
                        const hours = dateObj.getHours().toString().padStart(2, '0');
                        const minutes = dateObj.getMinutes().toString().padStart(2, '0');
                        startTimeStr = `${hours}:${minutes}`;
                    }
                    return {
                        id: `template-${Date.now()}-${index}`,
                        categoryName: cat.name,
                        startTime: startTimeStr,
                        durationMins: cat.duration > 0 ? Math.round(cat.duration / 60000) : 60,
                        distanceMiles: cat.distance || 0,
                        // Deliberately omitting originalRaceId so it saves as a new category
                        athletesCount: 0
                    };
                });
                setCategories(newCats);
            } else {
                alert("The selected race has no categories to copy.");
            }
        } catch (e) {
            console.error("Failed to load template race", e);
            alert("Failed to load categories from the selected race.");
        } finally {
            setIsLoadingTemplate(false);
        }
    };

    const handleAddCategory = () => {
        setCategories([...categories, {
            id: `new-${Date.now()}`,
            categoryName: '',
            startTime: '12:00',
            durationMins: 60,
            distanceMiles: 0
        }]);
    };

    const handleRemoveCategory = (id: string) => {
        if (categories.length === 1) {
            alert("At least one category is required.");
            return;
        }

        const categoryToRemove = categories.find(c => c.id === id);
        if (categoryToRemove && categoryToRemove.athletesCount && categoryToRemove.athletesCount > 0) {
            if (!window.confirm(`Are you sure you want to delete the category "${categoryToRemove.categoryName}"? This will permanently delete ${categoryToRemove.athletesCount} athlete(s) and their data.`)) {
                return;
            }
        }

        setCategories(categories.filter(c => c.id !== id));
    };

    const updateCategory = (id: string, field: keyof CategoryRow, value: any) => {
        setCategories(categories.map(c => c.id === id ? { ...c, [field]: value } : c));
    };

    const handleRouteFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setIsParsingRoute(true);
        try {
            const arrayBuffer = await file.arrayBuffer();
            const text = new TextDecoder().decode(arrayBuffer);

            let track: any[] = [];
            let path: [number, number][] = [];

            const fileNameLower = file.name.toLowerCase();
            if (fileNameLower.endsWith('.gpx')) {
                const parsed = parseGpxData(text);
                track = parsed.track;
                path = parsed.path;
            } else if (fileNameLower.endsWith('.tcx')) {
                const parsed = parseTcxData(text);
                track = parsed.track;
                path = parsed.path;
            } else {
                const parsed = await parseFitData(arrayBuffer);
                track = parsed.track;
                path = parsed.path;
            }

            if (!path || path.length === 0) {
                alert("No valid route track points found in file.");
                return;
            }

            setCoursePath(path);
            setRawCoursePath(path);
            rawTrackRef.current = track;
            setRouteFileName(file.name);
            setShowMap(true);

            // Check if start or finish coordinates were already designated by the admin
            const hasExistingStart = Boolean(startLat && startLon && (parseFloat(startLat) !== 0 || parseFloat(startLon) !== 0));
            const hasExistingFinish = Boolean(finishLat && finishLon && (parseFloat(finishLat) !== 0 || parseFloat(finishLon) !== 0));

            // Auto-populate start and finish coordinates only if not already designated by admin
            const sLat = path[0][0];
            const sLon = path[0][1];
            if (!hasExistingStart) {
                setStartLat(sLat.toFixed(6));
                setStartLon(sLon.toFixed(6));
            }
            if (!hasExistingFinish) {
                setFinishLat(path[path.length - 1][0].toFixed(6));
                setFinishLon(path[path.length - 1][1].toFixed(6));
            }

            // Auto-detect time zone from designated start location (or route start if none)
            const tzLat = hasExistingStart ? parseFloat(startLat) : sLat;
            const tzLon = hasExistingStart ? parseFloat(startLon) : sLon;
            raceEventService.getTimeZoneForLocation(tzLat, tzLon)
                .then(detectedTz => {
                    if (detectedTz) setTimeZone(detectedTz);
                })
                .catch(e => console.warn('Failed to detect timezone for coordinates', e));

            // Compute distance & elevation profile
            let totalDistMeters = 0;
            let totalElevGainMeters = 0;
            const profile: any[] = [];
            const hasAnyElevation = track.some((p: any) => p.elevation !== 0);

            for (let i = 0; i < track.length; i++) {
                const pt = track[i];
                if (i > 0) {
                    const prev = track[i - 1];
                    const elevDiff = pt.elevation - prev.elevation;
                    if (elevDiff > 0.05) totalElevGainMeters += elevDiff;
                }
                totalDistMeters = pt.distance || totalDistMeters;

                // Sample down to ~150 profile points
                if (i % Math.max(1, Math.floor(track.length / 150)) === 0 || i === track.length - 1) {
                    profile.push({
                        distanceMiles: Number(((pt.distance || 0) * 0.000621371).toFixed(2)),
                        elevationFt: Math.round(pt.elevation * 3.28084),
                        lat: pt.lat,
                        lon: pt.lon
                    });
                }
            }

            const distMiles = Number((totalDistMeters * 0.000621371).toFixed(1));

            setDistanceMiles(distMiles);
            setRawDistanceMiles(distMiles);

            let elevGainFt: number | undefined = undefined;
            if (hasAnyElevation) {
                elevGainFt = Math.round(totalElevGainMeters * 3.28084);
                setElevationGainFt(elevGainFt);
                setRawElevationGainFt(elevGainFt);
                setElevationProfile(profile);
            } else {
                // Route file has no elevation data — clear elevation state & notify user
                setElevationGainFt(undefined);
                setRawElevationGainFt(undefined);
                setElevationProfile([]);
                alert(
                    '⚠️ No Elevation Data\n\n' +
                    'This route file does not contain elevation information. ' +
                    'Some route planners (e.g. Hammerhead) do not embed elevation in their GPX exports.\n\n' +
                    'For elevation data, try exporting a .FIT or .GPX file from Garmin Connect, Strava, or RideWithGPS.'
                );
            }

            // Auto-trim if start and finish markers were already designated by the admin
            if (hasExistingStart && hasExistingFinish) {
                const sCoord: [number, number] = [parseFloat(startLat), parseFloat(startLon)];
                const fCoord: [number, number] = [parseFloat(finishLat), parseFloat(finishLon)];
                const trimResult = trimRouteSegment(path, sCoord, fCoord, track);
                if (trimResult && trimResult.trimmedPath.length > 1) {
                    setCoursePath(trimResult.trimmedPath);
                    setDistanceMiles(trimResult.distanceMiles);
                    if (trimResult.elevationGainFt !== undefined) setElevationGainFt(trimResult.elevationGainFt);
                    if (trimResult.elevationProfile.length > 0) setElevationProfile(trimResult.elevationProfile);
                    setIsRouteTrimmed(true);
                }
            } else {
                setIsRouteTrimmed(false);
            }

        } catch (err) {
            console.error("Failed to parse route file:", err);
            alert("Could not parse route file. Please upload a valid GPX, TCX, or FIT file.");
        } finally {
            setIsParsingRoute(false);
        }
    };

    const handleTrimRoute = () => {
        const sLat = parseFloat(startLat);
        const sLon = parseFloat(startLon);
        const fLat = parseFloat(finishLat);
        const fLon = parseFloat(finishLon);

        if (isNaN(sLat) || isNaN(sLon) || isNaN(fLat) || isNaN(fLon) || (sLat === 0 && sLon === 0) || (fLat === 0 && fLon === 0)) {
            alert("⚠️ Missing Start or Finish\n\nPlease set both Start/Meetup and Finish markers on the map first before trimming.");
            return;
        }

        const pathToUse = rawCoursePath.length > 0 ? rawCoursePath : coursePath;
        if (!pathToUse || pathToUse.length === 0) {
            alert("Please upload a route file first.");
            return;
        }

        if (rawCoursePath.length === 0) {
            setRawCoursePath(pathToUse);
        }

        const trackToUse = rawTrackRef.current && rawTrackRef.current.length === pathToUse.length ? rawTrackRef.current : undefined;
        const result = trimRouteSegment(pathToUse, [sLat, sLon], [fLat, fLon], trackToUse);

        if (result && result.trimmedPath.length > 1) {
            setCoursePath(result.trimmedPath);
            setDistanceMiles(result.distanceMiles);
            if (result.elevationGainFt !== undefined) {
                setElevationGainFt(result.elevationGainFt);
            }
            if (result.elevationProfile && result.elevationProfile.length > 0) {
                setElevationProfile(result.elevationProfile);
            }
            setIsRouteTrimmed(true);
        } else {
            alert("Could not trim route. Ensure start and finish markers are placed along the course.");
        }
    };

    const handleRevertRoute = () => {
        if (rawCoursePath.length === 0) return;
        setCoursePath(rawCoursePath);
        if (rawDistanceMiles !== undefined) setDistanceMiles(rawDistanceMiles);
        if (rawElevationGainFt !== undefined) setElevationGainFt(rawElevationGainFt);
        setIsRouteTrimmed(false);
    };

    const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (file.size > 5 * 1024 * 1024) {
            alert("File size too large. Please upload an image smaller than 5MB.");
            return;
        }

        try {
            const resizedDataUrl = await resizeImage(file, 600, 600);
            setImageUrl(resizedDataUrl);
        } catch (error) {
            console.error("Error resizing image:", error);
            alert("Failed to process image.");
        }
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        if (eventType === 'group_ride') {
            if (!eventName || dayOfWeek === undefined || !locationName || !meetupTime || !eventDate) {
                alert('Please fill in all required Group Ride fields (Name, Meetup Date, Meetup Location, Meetup Time).');
                return;
            }
            if (isRollTimeInvalid) {
                alert(`⚠️ Invalid Roll Time\n\nRoll time (${formatTime12h(rollTime)}) cannot be earlier than Meetup time (${formatTime12h(meetupTime)}). Please adjust the roll time.`);
                return;
            }
        } else {
            if (!eventName || !eventDate || categories.some(c => !c.categoryName || !c.startTime)) {
                alert('Please fill in all required fields (Event Name, Date, Category Names, Start Times).');
                return;
            }
        }

        const courseStart: [number, number] = [parseFloat(startLat) || 0, parseFloat(startLon) || 0];
        const courseFinish: [number, number] = [parseFloat(finishLat) || 0, parseFloat(finishLon) || 0];

        const eventId = eventToEdit?.id || `${generateSlug(eventName)}-${Date.now()}`;

        let raceCategories: RaceCategory[] = [];

        if (eventType === 'group_ride') {
            const timeString = meetupTime.length === 5 ? `${meetupTime}:00` : meetupTime;
            const startDateTime = eventDate ? new Date(`${eventDate}T${timeString}`) : new Date();
            const scheduledTimestamp = isNaN(startDateTime.getTime()) ? 0 : startDateTime.getTime();

            raceCategories = [{
                id: eventToEdit?.categories?.[0]?.id || generateSlug('Group Ride'),
                name: 'Group Ride',
                scheduledStartTime: scheduledTimestamp,
                startTime: eventToEdit?.categories?.[0]?.startTime || 0,
                duration: 90 * 60 * 1000,
                distance: 0,
                athletes: eventToEdit?.categories?.[0]?.athletes || [],
            }];
        } else {
            raceCategories = categories.map(cat => {
                const timeString = cat.startTime.length === 5 ? `${cat.startTime}:00` : cat.startTime;
                const startDateTime = new Date(`${eventDate}T${timeString}`);
                const scheduledTimestamp = isNaN(startDateTime.getTime()) ? 0 : startDateTime.getTime();

                let originalAthletes: Athlete[] = [];
                let existingStartTime = 0;

                if (isEditing && eventToEdit) {
                    const originalCat = eventToEdit.categories.find(c => c.id === cat.originalRaceId);
                    if (originalCat) {
                        originalAthletes = originalCat.athletes;
                        existingStartTime = originalCat.startTime || 0;
                    }
                }

                return {
                    id: cat.originalRaceId || generateSlug(cat.categoryName),
                    name: cat.categoryName,
                    scheduledStartTime: scheduledTimestamp,
                    startTime: existingStartTime,
                    duration: cat.durationMins * 60 * 1000,
                    distance: cat.distanceMiles,
                    athletes: originalAthletes,
                };
            });
        }

        const groupRideDetails: GroupRideDetails | undefined = eventType === 'group_ride' ? {
            frequency,
            dayOfWeek: Number(dayOfWeek),
            meetupTime,
            rollTime,
            locationName,
            isDrop,
            skillLevel,
            hostName,
            hostUrl,
            infoText,
            distanceMiles,
            elevationGainFt,
            elevationProfile,
            timeZone: timeZone || eventToEdit?.groupRideDetails?.timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone,
            spiritedSegment: (spiritedStartLat && spiritedStartLon && spiritedFinishLat && spiritedFinishLon) ? (() => {
                const raw = {
                    startLat: parseFloat(spiritedStartLat),
                    startLon: parseFloat(spiritedStartLon),
                    finishLat: parseFloat(spiritedFinishLat),
                    finishLon: parseFloat(spiritedFinishLon),
                };
                return normalizeSector(raw, coursePath) || raw;
            })() : undefined
        } : undefined;

        const raceEvent: RaceEvent = {
            id: eventId,
            // Keep the existing slug on edit; otherwise saveRaceEvent regenerates one and the URL changes
            slug: eventToEdit?.slug,
            name: eventName,
            date: eventDate || new Date().toISOString().split('T')[0],
            imageUrl: imageUrl,
            ownerId: eventToEdit?.ownerId || currentUser?.uid || '',
            course: {
                path: coursePath.length > 0 ? coursePath : (eventToEdit?.course?.path || []),
                start: courseStart,
                finish: courseFinish,
            },
            isCircuit: isCircuit,
            eventType: eventType,
            groupRideDetails: groupRideDetails,
            isExternal: eventToEdit?.isExternal ?? false,
            websiteUrl: eventToEdit?.websiteUrl,
            externalId: eventToEdit?.externalId,
            externalSource: eventToEdit?.externalSource,
            categoryCount: raceCategories.length,
            categories: raceCategories,
        };

        onSave(raceEvent);
    };

    const handleDeleteClick = () => {
        if (onDelete && eventToEdit) {
            setIsConfirmingDelete(true);
        }
    }

    const handleConfirmDelete = () => {
        if (onDelete && eventToEdit) {
            onDelete(eventToEdit);
        }
    }

    const handleMapStartChange = (lat: number, lon: number) => {
        setStartLat(lat.toFixed(6));
        setStartLon(lon.toFixed(6));
        raceEventService.getTimeZoneForLocation(lat, lon)
            .then(detectedTz => {
                if (detectedTz) setTimeZone(detectedTz);
            })
            .catch(e => console.warn('Failed to detect timezone for map coordinates', e));
    };

    const handleMapFinishChange = (lat: number, lon: number) => {
        setFinishLat(lat.toFixed(6));
        setFinishLon(lon.toFixed(6));
    };

    const handleAutoDetect = async () => {
        if (!eventToEdit) return;

        setIsDetecting(true);
        try {
            const functions = getFunctions();
            const detectRaceLocations = httpsCallable(functions, 'detectRaceLocations');

            // Aggregate all athletes from all categories
            const allAthletes = eventToEdit.categories.flatMap(c => c.athletes || []);

            // Pass raceId so backend can look up existing course
            const raceId = eventToEdit.id;

            const result: any = await detectRaceLocations({ raceId, athletes: allAthletes });
            const { start, finish, isCircuit: detectedIsCircuit } = result.data;

            if (start) {
                setStartLat(start.lat.toFixed(6));
                setStartLon(start.lon.toFixed(6));
            }
            if (finish) {
                setFinishLat(finish.lat.toFixed(6));
                setFinishLon(finish.lon.toFixed(6));
            }
            if (typeof detectedIsCircuit === 'boolean') {
                setIsCircuit(detectedIsCircuit);
            }
        } catch (error) {
            console.error("Error detecting locations:", error);
            alert("Failed to auto-detect locations.");
        } finally {
            setIsDetecting(false);
        }
    };

    const meetupMins = parseTimeToMinutes(meetupTime);
    const rollMins = parseTimeToMinutes(rollTime);
    const isRollTimeInvalid = eventType === 'group_ride' && meetupMins !== null && rollMins !== null && rollMins < meetupMins;

    const isPublishDisabled = eventType === 'group_ride'
        ? (!eventName || !locationName || !meetupTime || !eventDate || isRollTimeInvalid)
        : (!eventName || !eventDate || categories.length === 0);

    return (
        <>
            <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4" aria-modal="true" role="dialog" onClick={onClose}>
                <div
                    className={`bg-gray-800 rounded-lg shadow-xl p-6 w-full ${showMap ? 'max-w-7xl' : 'max-w-3xl'} border border-gray-700 relative transition-all duration-300 max-h-[90vh] overflow-y-auto`}
                    onClick={(e) => e.stopPropagation()}
                >
                    <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-white" aria-label="Close modal">
                        <XIcon className="w-6 h-6" />
                    </button>
                    <h2 className="text-2xl font-bold text-cyan-400 mb-4">{isEditing ? 'Edit Event' : 'Create New Event'}</h2>

                    {/* Top Segmented Mode Switcher */}
                    <div className="flex bg-gray-900/80 p-1 rounded-lg border border-gray-700 mb-6 max-w-md">
                        <button
                            type="button"
                            onClick={() => setEventType('race')}
                            className={`flex-1 py-2 px-4 rounded-md font-semibold text-sm transition-all flex items-center justify-center gap-2 ${eventType === 'race' ? 'bg-cyan-500 text-gray-950 shadow-md' : 'text-gray-400 hover:text-white'}`}
                        >
                            <span>🏁</span> Race
                        </button>
                        <button
                            type="button"
                            onClick={() => {
                                setEventType('group_ride');
                                if (!eventDate) {
                                    const todayStr = formatDateToString(new Date());
                                    setEventDate(todayStr);
                                    setDayOfWeek(getDayOfWeekFromDateString(todayStr));
                                } else {
                                    setDayOfWeek(getDayOfWeekFromDateString(eventDate));
                                }
                            }}
                            className={`flex-1 py-2 px-4 rounded-md font-semibold text-sm transition-all flex items-center justify-center gap-2 ${eventType === 'group_ride' ? 'bg-cyan-500 text-gray-950 shadow-md' : 'text-gray-400 hover:text-white'}`}
                        >
                            <span>🚴</span> Group Ride
                        </button>
                    </div>

                    <form onSubmit={handleSubmit}>
                        <div className={`grid gap-6 ${showMap ? 'grid-cols-1 lg:grid-cols-2' : 'grid-cols-1'}`}>

                            {/* Left Column: Event Details */}
                            <div className="space-y-6">
                                {/* Base Event Details Section */}
                                <div className="bg-gray-700/30 p-4 rounded-lg border border-gray-600 space-y-4">
                                    <h3 className="text-sm font-bold text-gray-200 uppercase tracking-wide border-b border-gray-600 pb-2">
                                        {eventType === 'group_ride' ? 'Group Ride Info' : 'Event Details'}
                                    </h3>
                                    <div>
                                        <label htmlFor="event-name" className="block text-sm font-medium text-gray-300">
                                            {eventType === 'group_ride' ? 'Ride Name *' : 'Event Name *'}
                                        </label>
                                        <input
                                            type="text"
                                            id="event-name"
                                            value={eventName}
                                            onChange={(e) => setEventName(e.target.value)}
                                            placeholder={eventType === 'group_ride' ? 'e.g. Roasters Weekly Tuesday Ride' : 'e.g. Emerald City Criterium'}
                                            required
                                            className="mt-1 block w-full bg-gray-700 border border-gray-600 rounded-md shadow-sm py-2 px-3 text-white focus:outline-none focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm"
                                        />
                                    </div>

                                    {eventType === 'race' ? (
                                        <div>
                                            <label htmlFor="date" className="block text-sm font-medium text-gray-300">Race Date *</label>
                                            <input
                                                type="date"
                                                id="date"
                                                value={eventDate}
                                                onChange={(e) => setEventDate(e.target.value)}
                                                required
                                                className="mt-1 block w-full bg-gray-700 border border-gray-600 rounded-md shadow-sm py-2 px-3 text-white focus:outline-none focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm"
                                            />
                                        </div>
                                    ) : (
                                        /* Group Ride Schedule & Recurrence Card */
                                        <div className="bg-gray-800/80 p-4 rounded-lg border border-gray-600/80 space-y-4">
                                            <div className="flex items-center justify-between border-b border-gray-700/80 pb-2">
                                                <label className="block text-xs font-bold text-cyan-400 uppercase tracking-wider flex items-center gap-1.5">
                                                    <span>📅</span> Schedule & Recurrence
                                                </label>
                                                <span className="text-[11px] text-gray-400 font-medium">Synced to calendar date</span>
                                            </div>

                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                                <div>
                                                    <label className="block text-xs font-medium text-gray-300 mb-1">Recurrence Frequency *</label>
                                                    <select
                                                        value={frequency}
                                                        onChange={(e) => setFrequency(e.target.value as RecurrenceFrequency)}
                                                        className="w-full bg-gray-700 border border-gray-600 rounded-md py-2 px-3 text-white text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
                                                    >
                                                        <option value="weekly">Weekly</option>
                                                        <option value="biweekly">Bi-Weekly (Every 2 Weeks)</option>
                                                        <option value="monthly">Monthly</option>
                                                    </select>
                                                </div>

                                                <div>
                                                    <label className="block text-xs font-medium text-gray-300 mb-1">Next Meetup Date *</label>
                                                    <input
                                                        type="date"
                                                        value={eventDate}
                                                        onChange={(e) => handleMeetupDateChange(e.target.value)}
                                                        required
                                                        className="w-full bg-gray-700 border border-gray-600 rounded-md py-2 px-3 text-white text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
                                                    />
                                                </div>
                                            </div>

                                            <div>
                                                <div className="flex justify-between items-center mb-1.5">
                                                    <label className="block text-xs font-medium text-gray-300">Repeat Day</label>
                                                    <span className="text-xs text-cyan-400 font-semibold flex items-center gap-1">
                                                        <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-pulse"></span>
                                                        {DAYS_OF_WEEK.find(d => d.value === dayOfWeek)?.label}s
                                                    </span>
                                                </div>

                                                <div className="grid grid-cols-7 gap-1.5">
                                                    {DAYS_OF_WEEK.map((d) => {
                                                        const isSelected = dayOfWeek === d.value;
                                                        return (
                                                            <button
                                                                key={d.value}
                                                                type="button"
                                                                onClick={() => handleDayOfWeekChange(d.value)}
                                                                className={`py-2 text-xs font-bold rounded-md transition-all flex flex-col items-center justify-center border ${isSelected
                                                                        ? 'bg-cyan-500 text-gray-950 border-cyan-400 shadow-md shadow-cyan-500/20 scale-[1.02]'
                                                                        : 'bg-gray-700/80 text-gray-300 border-gray-600 hover:bg-gray-600 hover:text-white'
                                                                    }`}
                                                                title={`Select ${d.label}`}
                                                            >
                                                                <span>{d.short}</span>
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                            </div>

                                            <div className="bg-cyan-950/40 border border-cyan-500/30 rounded-md p-2.5 flex items-center gap-2.5 text-xs text-cyan-200">
                                                <span className="text-base flex-shrink-0">🗓️</span>
                                                <div className="flex-1 leading-tight">
                                                    <span className="font-semibold text-cyan-300">Schedule Rule: </span>
                                                    <span>{getRecurrenceSummaryText(frequency, dayOfWeek, eventDate)}</span>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {eventType === 'group_ride' && (
                                        <>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-300">Meetup Location *</label>
                                                <input
                                                    type="text"
                                                    value={locationName}
                                                    onChange={(e) => setLocationName(e.target.value)}
                                                    placeholder="e.g. Roasters Coffee, 1st Ave"
                                                    required
                                                    className="mt-1 block w-full bg-gray-700 border border-gray-600 rounded-md py-2 px-3 text-white text-sm"
                                                />
                                            </div>

                                            <div className="space-y-1">
                                                <div className="grid grid-cols-2 gap-4">
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-300">Meetup Time *</label>
                                                        <input
                                                            type="time"
                                                            value={meetupTime}
                                                            onChange={(e) => handleMeetupTimeChange(e.target.value)}
                                                            required
                                                            className="mt-1 block w-full bg-gray-700 border border-gray-600 rounded-md py-2 px-3 text-white text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-300 flex items-center justify-between">
                                                            <span>Roll Time</span>
                                                            <span className="text-[10px] text-gray-400 font-normal">On or after meetup</span>
                                                        </label>
                                                        <input
                                                            type="time"
                                                            value={rollTime}
                                                            min={meetupTime}
                                                            onChange={(e) => handleRollTimeChange(e.target.value)}
                                                            className={`mt-1 block w-full border rounded-md py-2 px-3 text-sm focus:outline-none focus:ring-2 ${isRollTimeInvalid
                                                                    ? 'bg-red-950/50 border-red-500 text-red-200 focus:ring-red-500'
                                                                    : 'bg-gray-700 border-gray-600 text-white focus:ring-cyan-500'
                                                                }`}
                                                        />
                                                    </div>
                                                </div>
                                                {meetupTime && rollTime && (
                                                    <div className={`text-[11px] font-medium px-0.5 pt-0.5 flex items-center gap-1 ${isRollTimeInvalid ? 'text-red-400 font-bold' : 'text-cyan-400'
                                                        }`}>
                                                        <span>{isRollTimeInvalid ? '⚠️' : '⏱️'}</span>
                                                        <span>
                                                            {getMeetupBufferText(meetupTime, rollTime)}
                                                            {timeZone && ` (${getTimeZoneAbbreviation(timeZone)})`}
                                                        </span>
                                                    </div>
                                                )}
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-300 flex items-center justify-between">
                                                    <span>Ride Time Zone</span>
                                                    <span className="text-[11px] text-cyan-400 font-mono font-medium">
                                                        {timeZone ? getTimeZoneAbbreviation(timeZone) : ''}
                                                    </span>
                                                </label>
                                                <select
                                                    value={timeZone}
                                                    onChange={(e) => setTimeZone(e.target.value)}
                                                    className="mt-1 block w-full bg-gray-700 border border-gray-600 rounded-md py-2 px-3 text-white text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
                                                >
                                                    <optgroup label="United States & Canada">
                                                        {COMMON_TIMEZONES.slice(0, 7).map(tz => (
                                                            <option key={tz.value} value={tz.value}>{tz.label}</option>
                                                        ))}
                                                    </optgroup>
                                                    <optgroup label="International">
                                                        {COMMON_TIMEZONES.slice(7).map(tz => (
                                                            <option key={tz.value} value={tz.value}>{tz.label}</option>
                                                        ))}
                                                    </optgroup>
                                                    {!COMMON_TIMEZONES.some(tz => tz.value === timeZone) && (
                                                        <optgroup label="Other">
                                                            <option value={timeZone}>{timeZone}</option>
                                                        </optgroup>
                                                    )}
                                                </select>
                                                <p className="text-[11px] text-gray-400 mt-1">
                                                    Automatically detected from course start coordinates. You can adjust if needed.
                                                </p>
                                            </div>

                                            <div className="grid grid-cols-2 gap-4">
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-300">Ride Policy</label>
                                                    <select
                                                        value={isDrop ? 'drop' : 'no_drop'}
                                                        onChange={(e) => setIsDrop(e.target.value === 'drop')}
                                                        className="mt-1 block w-full bg-gray-700 border border-gray-600 rounded-md py-2 px-3 text-white text-sm"
                                                    >
                                                        <option value="no_drop">No Drop (Regrouping)</option>
                                                        <option value="drop">Drop Ride (Spirited / Fast)</option>
                                                    </select>
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-300">Skill Tier</label>
                                                    <select
                                                        value={skillLevel}
                                                        onChange={(e) => setSkillLevel(e.target.value as SkillLevel)}
                                                        className="mt-1 block w-full bg-gray-700 border border-gray-600 rounded-md py-2 px-3 text-white text-sm"
                                                    >
                                                        <option value="all_levels">All Levels</option>
                                                        <option value="beginner">Beginner</option>
                                                        <option value="intermediate">Intermediate</option>
                                                        <option value="advanced">Advanced / Fast</option>
                                                    </select>
                                                </div>
                                            </div>

                                            <div className="bg-gray-800/80 p-3.5 rounded-lg border border-gray-600 space-y-2.5">
                                                <div className="flex justify-between items-center">
                                                    <label className="block text-xs font-bold text-gray-200 uppercase tracking-wide">
                                                        Route File (.gpx, .fit, .tcx)
                                                    </label>
                                                    {coursePath.length > 0 && isRouteTrimmed && (
                                                        <span className="text-[10px] font-bold px-2 py-0.5 bg-emerald-950 text-emerald-300 border border-emerald-500/50 rounded-full flex items-center gap-1">
                                                            <span>✂️</span> Trimmed to Group Ride
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 bg-gray-900/90 p-2.5 rounded border border-gray-700">
                                                    {coursePath.length > 0 ? (
                                                        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
                                                            <div className={`px-2.5 py-1 ${isRouteTrimmed ? 'bg-emerald-950 text-emerald-300 border-emerald-500/40' : 'bg-cyan-950 text-cyan-300 border-cyan-500/40'} border rounded text-xs font-bold flex items-center gap-1`}>
                                                                <span>{isRouteTrimmed ? '🏁' : '🗺️'}</span> {isRouteTrimmed ? 'Active Route' : 'Route Loaded'}
                                                            </div>
                                                            <div className="text-xs">
                                                                <span className="font-bold text-white">{distanceMiles ? `${distanceMiles} mi` : ''}</span>
                                                                {elevationGainFt !== undefined && <span className="text-gray-400 font-medium ml-1.5">• +{elevationGainFt.toLocaleString()} ft Elev</span>}
                                                                <span className="text-gray-500 text-[10px] ml-1.5">({coursePath.length} pts)</span>
                                                                {isRouteTrimmed && rawDistanceMiles !== undefined && rawDistanceMiles > (distanceMiles || 0) && (
                                                                    <div className="text-[10px] text-gray-400 mt-0.5">
                                                                        Original activity: {rawDistanceMiles} mi (trimmed {(rawDistanceMiles - (distanceMiles || 0)).toFixed(1)} commute mi)
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <span className="text-xs text-gray-400">Upload GPX or FIT file to render route map & elevation chart</span>
                                                    )}

                                                    <div className="flex items-center gap-2 self-end sm:self-auto">
                                                        {routeFileName && (
                                                            <span className="text-[11px] text-gray-400 truncate max-w-[120px] hidden sm:inline" title={routeFileName}>
                                                                {routeFileName}
                                                            </span>
                                                        )}
                                                        <input
                                                            type="file"
                                                            ref={routeInputRef}
                                                            onChange={handleRouteFileUpload}
                                                            accept=".gpx,.fit,.tcx"
                                                            className="hidden"
                                                        />
                                                        <button
                                                            type="button"
                                                            onClick={() => routeInputRef.current?.click()}
                                                            disabled={isParsingRoute}
                                                            className="px-3 py-1.5 bg-cyan-600 hover:bg-cyan-500 text-white text-xs font-bold rounded transition-colors disabled:opacity-50 flex items-center gap-1 whitespace-nowrap"
                                                        >
                                                            {isParsingRoute ? 'Parsing...' : (coursePath.length > 0 ? 'Replace GPX' : 'Upload Route GPX')}
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="grid grid-cols-2 gap-4">
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-300">Host / Sponsor Name</label>
                                                    <input
                                                        type="text"
                                                        value={hostName}
                                                        onChange={(e) => setHostName(e.target.value)}
                                                        placeholder="e.g. Roasters Cycling Club"
                                                        className="mt-1 block w-full bg-gray-700 border border-gray-600 rounded-md py-2 px-3 text-white text-sm"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-300">Host Website URL</label>
                                                    <input
                                                        type="url"
                                                        value={hostUrl}
                                                        onChange={(e) => setHostUrl(e.target.value)}
                                                        placeholder="https://..."
                                                        className="mt-1 block w-full bg-gray-700 border border-gray-600 rounded-md py-2 px-3 text-white text-sm"
                                                    />
                                                </div>
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-300">Ride Guide & Details (Markdown / Text)</label>
                                                <textarea
                                                    rows={3}
                                                    value={infoText}
                                                    onChange={(e) => setInfoText(e.target.value)}
                                                    placeholder="Detail the route flow, regroup stops, pace expectations, and post-ride coffee..."
                                                    className="mt-1 block w-full bg-gray-700 border border-gray-600 rounded-md py-2 px-3 text-white text-sm"
                                                />
                                            </div>
                                        </>
                                    )}

                                    <div>
                                        <label className="block text-sm font-medium text-gray-300 mb-2">Event Image</label>
                                        <div className="flex gap-4 items-start">
                                            <div className="relative w-24 h-24 bg-gray-800 border border-gray-600 rounded-lg overflow-hidden flex-shrink-0 flex items-center justify-center group">
                                                {imageUrl ? (
                                                    <>
                                                        <img src={imageUrl} alt="Preview" className="w-full h-full object-cover" />
                                                        <button
                                                            type="button"
                                                            onClick={() => setImageUrl('')}
                                                            className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 flex items-center justify-center text-white transition-opacity"
                                                            title="Remove Image"
                                                        >
                                                            <TrashIcon className="w-5 h-5" />
                                                        </button>
                                                    </>
                                                ) : (
                                                    <span className="text-xs text-gray-500 text-center px-1">No Image</span>
                                                )}
                                            </div>
                                            <div className="flex-grow space-y-2">
                                                <div className="flex items-center gap-2">
                                                    <input
                                                        type="file"
                                                        ref={fileInputRef}
                                                        onChange={handleImageUpload}
                                                        accept="image/*"
                                                        className="hidden"
                                                    />
                                                    <button
                                                        type="button"
                                                        onClick={() => fileInputRef.current?.click()}
                                                        className="px-3 py-1.5 bg-gray-700 hover:bg-gray-600 text-white text-xs font-medium rounded border border-gray-600 transition-colors flex items-center gap-2"
                                                    >
                                                        <PlusIcon className="w-3 h-3" />
                                                        Upload Image
                                                    </button>
                                                    <span className="text-[10px] text-gray-500">Max 5MB</span>
                                                </div>
                                                <input
                                                    type="url"
                                                    placeholder="Or paste image URL..."
                                                    value={imageUrl.startsWith('data:') ? '' : imageUrl}
                                                    onChange={(e) => setImageUrl(e.target.value)}
                                                    disabled={imageUrl.startsWith('data:')}
                                                    className="block w-full bg-gray-700 border border-gray-600 rounded-md shadow-sm py-1.5 px-3 text-white text-xs focus:outline-none focus:ring-cyan-500 focus:border-cyan-500 disabled:opacity-50"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex justify-between items-center pt-2">
                                        <h3 className="text-sm font-medium text-gray-300">Course Map</h3>
                                        <button
                                            type="button"
                                            onClick={() => setShowMap(!showMap)}
                                            className={`flex items-center gap-1.5 px-3 py-1 text-xs font-bold rounded-full border transition-colors ${showMap ? 'bg-cyan-600 text-white border-cyan-500' : 'bg-gray-700 text-cyan-400 border-gray-600 hover:border-cyan-500'}`}
                                        >
                                            <MapPinIcon className="w-3.5 h-3.5" />
                                            {showMap ? 'Hide Map Tool' : 'Set Start/Finish Lines'}
                                        </button>
                                    </div>
                                </div>

                                {/* Race Categories Section (Only when EventType is Race) */}
                                {eventType === 'race' && (
                                    <div className="bg-gray-700/30 p-4 rounded-lg border border-gray-600 space-y-4">
                                        <div className="flex justify-between items-center border-b border-gray-600 pb-2">
                                            <h3 className="text-sm font-bold text-gray-200 uppercase tracking-wide">Race Categories</h3>
                                            <button
                                                type="button"
                                                onClick={handleAddCategory}
                                                className="text-xs flex items-center gap-1 text-cyan-400 hover:text-white transition-colors"
                                            >
                                                <PlusIcon className="w-4 h-4" />
                                                Add Category
                                            </button>
                                        </div>

                                        {/* Template Selector */}
                                        <div className="flex items-center gap-2 bg-gray-800 p-2.5 rounded border border-gray-700 mt-3 mb-2">
                                            <span className="text-xs text-gray-400 font-medium whitespace-nowrap">Template:</span>
                                            <select
                                                value={selectedTemplateRaceId}
                                                onChange={(e) => setSelectedTemplateRaceId(e.target.value)}
                                                className="flex-grow bg-gray-700 border border-gray-600 rounded px-2 py-1 text-xs text-white focus:ring-1 focus:ring-cyan-500 focus:outline-none"
                                            >
                                                <option value="">Select a race to copy categories from...</option>
                                                {availableRaces.map(r => (
                                                    <option key={r.id} value={r.id}>{r.name} ({new Date(r.date).toLocaleDateString()})</option>
                                                ))}
                                            </select>
                                            <button
                                                type="button"
                                                onClick={handleApplyTemplate}
                                                disabled={!selectedTemplateRaceId || isLoadingTemplate}
                                                className="px-3 py-1 bg-cyan-600 text-white text-xs font-bold rounded hover:bg-cyan-500 disabled:opacity-50 transition-colors whitespace-nowrap"
                                            >
                                                {isLoadingTemplate ? 'Loading...' : 'Apply'}
                                            </button>
                                        </div>

                                        <div className="space-y-3">
                                            {categories.map((cat) => (
                                                <div key={cat.id} className="flex flex-wrap gap-2 items-end bg-gray-800 p-3 rounded border border-gray-700">
                                                    <div className="flex-grow min-w-[150px]">
                                                        <label className="block text-xs text-gray-400 mb-1">Category Name</label>
                                                        <input
                                                            type="text"
                                                            required
                                                            placeholder="e.g. Pro 1/2"
                                                            value={cat.categoryName}
                                                            onChange={(e) => updateCategory(cat.id, 'categoryName', e.target.value)}
                                                            className="w-full bg-gray-700 border border-gray-600 rounded px-2 py-1 text-sm text-white focus:ring-1 focus:ring-cyan-500 focus:outline-none"
                                                        />
                                                    </div>
                                                    <div className="w-24">
                                                        <label className="block text-xs text-gray-400 mb-1">Scheduled start time</label>
                                                        <input
                                                            type="time"
                                                            required
                                                            value={cat.startTime}
                                                            onChange={(e) => updateCategory(cat.id, 'startTime', e.target.value)}
                                                            className="w-full bg-gray-700 border border-gray-600 rounded px-2 py-1 text-sm text-white focus:ring-1 focus:ring-cyan-500 focus:outline-none"
                                                        />
                                                    </div>
                                                    <div className="w-20">
                                                        <label className="block text-xs text-gray-400 mb-1">Duration</label>
                                                        <div className="relative">
                                                            <input
                                                                type="number"
                                                                min="1"
                                                                value={cat.durationMins}
                                                                onChange={(e) => updateCategory(cat.id, 'durationMins', parseInt(e.target.value) || 0)}
                                                                className="w-full bg-gray-700 border border-gray-600 rounded px-2 py-1 text-sm text-white focus:ring-1 focus:ring-cyan-500 focus:outline-none"
                                                            />
                                                            <span className="absolute right-1 top-1.5 text-[10px] text-gray-500">min</span>
                                                        </div>
                                                    </div>
                                                    <div className="w-20">
                                                        <label className="block text-xs text-gray-400 mb-1">Dist (mi)</label>
                                                        <input
                                                            type="number"
                                                            min="0"
                                                            step="0.1"
                                                            value={cat.distanceMiles}
                                                            onChange={(e) => updateCategory(cat.id, 'distanceMiles', parseFloat(e.target.value) || 0)}
                                                            className="w-full bg-gray-700 border border-gray-600 rounded px-2 py-1 text-sm text-white focus:ring-1 focus:ring-cyan-500 focus:outline-none"
                                                        />
                                                    </div>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleRemoveCategory(cat.id)}
                                                        className="mb-1 p-1.5 text-gray-500 hover:text-red-400 hover:bg-red-900/20 rounded transition-colors"
                                                        title="Remove Category"
                                                    >
                                                        <TrashIcon className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Right Column: Map Tool (Conditional) */}
                            {showMap && (
                                <div className="flex flex-col lg:self-start lg:sticky lg:top-0 space-y-3">
                                    <RaceLocationPickerMap
                                        startLat={parseFloat(startLat)}
                                        startLon={parseFloat(startLon)}
                                        finishLat={parseFloat(finishLat)}
                                        finishLon={parseFloat(finishLon)}
                                        onStartChange={handleMapStartChange}
                                        onFinishChange={handleMapFinishChange}
                                        coursePath={coursePath}
                                        rawCoursePath={rawCoursePath}
                                        isTrimmed={isRouteTrimmed}
                                        onTrimRoute={handleTrimRoute}
                                        onRevertRoute={handleRevertRoute}
                                        eventType={eventType}
                                        spiritedStartLat={parseFloat(spiritedStartLat)}
                                        spiritedStartLon={parseFloat(spiritedStartLon)}
                                        spiritedFinishLat={parseFloat(spiritedFinishLat)}
                                        spiritedFinishLon={parseFloat(spiritedFinishLon)}
                                        onSpiritedStartChange={(lat, lon) => {
                                            setSpiritedStartLat(lat.toFixed(6));
                                            setSpiritedStartLon(lon.toFixed(6));
                                        }}
                                        onSpiritedFinishChange={(lat, lon) => {
                                            setSpiritedFinishLat(lat.toFixed(6));
                                            setSpiritedFinishLon(lon.toFixed(6));
                                        }}
                                        onAutoDetect={isEditing ? handleAutoDetect : undefined}
                                        isDetecting={isDetecting}
                                    />
                                    <div className="grid grid-cols-2 gap-3 text-xs text-gray-400">
                                        <div className="bg-gray-700/50 p-2 rounded border border-gray-600">
                                            <span className="block font-bold text-gray-300">Start / Meetup Coordinates</span>
                                            <span className="font-mono text-cyan-300">{startLat && startLon ? `${startLat}, ${startLon}` : 'Not Set'}</span>
                                        </div>
                                        <div className="bg-gray-700/50 p-2 rounded border border-gray-600">
                                            <span className="block font-bold text-gray-300">Finish Coordinates</span>
                                            <span className="font-mono text-emerald-300">{finishLat && finishLon ? `${finishLat}, ${finishLon}` : 'Not Set'}</span>
                                        </div>
                                    </div>

                                    {eventType === 'group_ride' && (
                                        <div className="mt-3 bg-cyan-950/40 p-3 rounded-lg border border-cyan-700/50 space-y-2">
                                            <div className="flex justify-between items-center">
                                                <span className="font-bold text-xs text-cyan-300 uppercase tracking-wide flex items-center gap-1.5">
                                                    <span>⚡</span> Race Sector
                                                </span>
                                                {(spiritedStartLat || spiritedFinishLat) && (
                                                    <div className="flex items-center gap-2">
                                                        {spiritedStartLat && spiritedFinishLat && (
                                                            <button
                                                                type="button"
                                                                onClick={() => {
                                                                    const prevStartLat = spiritedStartLat;
                                                                    const prevStartLon = spiritedStartLon;
                                                                    setSpiritedStartLat(spiritedFinishLat);
                                                                    setSpiritedStartLon(spiritedFinishLon);
                                                                    setSpiritedFinishLat(prevStartLat);
                                                                    setSpiritedFinishLon(prevStartLon);
                                                                }}
                                                                className="text-[10px] text-cyan-400 hover:text-white underline font-semibold"
                                                            >
                                                                Swap Start/End
                                                            </button>
                                                        )}
                                                        <button
                                                            type="button"
                                                            onClick={() => {
                                                                setSpiritedStartLat('');
                                                                setSpiritedStartLon('');
                                                                setSpiritedFinishLat('');
                                                                setSpiritedFinishLon('');
                                                            }}
                                                            className="text-[10px] text-gray-400 hover:text-white underline font-semibold"
                                                        >
                                                            Reset
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                            <div className="grid grid-cols-2 gap-2 text-[11px]">
                                                <div className="bg-gray-900/80 p-2 rounded border border-cyan-900/60">
                                                    <span className="block text-gray-400 text-[10px]">⚡ Sector Start</span>
                                                    <span className="font-mono text-cyan-300 font-bold">
                                                        {spiritedStartLat && spiritedStartLon ? `${spiritedStartLat}, ${spiritedStartLon}` : 'Click map to set'}
                                                    </span>
                                                </div>
                                                <div className="bg-gray-900/80 p-2 rounded border border-cyan-900/60">
                                                    <span className="block text-gray-400 text-[10px]">🏆 Sector End</span>
                                                    <span className="font-mono text-cyan-300 font-bold">
                                                        {spiritedFinishLat && spiritedFinishLon ? `${spiritedFinishLat}, ${spiritedFinishLon}` : 'Click map to set'}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    <div className="mt-3 px-1">
                                        <label className="flex items-center gap-2 text-xs text-gray-300 hover:text-white cursor-pointer select-none w-fit">
                                            <input
                                                type="checkbox"
                                                checked={isCircuit}
                                                onChange={(e) => setIsCircuit(e.target.checked)}
                                                className="rounded bg-gray-700 border-gray-600 text-cyan-500 focus:ring-cyan-500 focus:ring-offset-gray-800"
                                            />
                                            Is Circuit (Laps)
                                        </label>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="flex justify-between items-center pt-6 mt-6 border-t border-gray-700">
                            <div>
                                {isEditing && (
                                    <button type="button" onClick={handleDeleteClick} className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-red-400 bg-gray-700/50 rounded-md hover:bg-red-900/50 hover:text-white transition-colors border border-transparent hover:border-red-800">
                                        <TrashIcon className="w-4 h-4" />
                                        Delete Event
                                    </button>
                                )}
                            </div>
                            <div className="flex justify-end gap-4">
                                <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-300 bg-gray-700 rounded-md hover:bg-gray-600 transition-colors">
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="px-6 py-2 text-sm font-bold text-gray-900 bg-cyan-500 rounded-md hover:bg-cyan-400 transition-colors disabled:opacity-50 shadow-lg shadow-cyan-500/20"
                                    disabled={isPublishDisabled}
                                >
                                    {isEditing ? 'Save Changes' : (eventType === 'group_ride' ? 'Publish Ride' : 'Create Event')}
                                </button>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
            <ConfirmationModal
                isOpen={isConfirmingDelete}
                onClose={() => setIsConfirmingDelete(false)}
                onConfirm={handleConfirmDelete}
                title="Confirm Deletion"
                message="Are you sure you want to delete this event? This will delete ALL categories and associated athlete data."
                confirmButtonText="Delete Event"
            />
        </>
    );
};

export default RaceEditorModal;
